import { useEffect, useState } from 'react'
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CategoryIcon from '@mui/icons-material/Category'
import WarningIcon from '@mui/icons-material/Warning'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  activeLinkedServiceChanged,
  fetchCourses,
  fetchLinkedServices,
  selectCourses,
  selectCoursesStatus,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  RecertificationCycleTemplate,
  RecertificationCycleTemplateCategory,
  RecertificationCycleTemplateCourse,
} from 'src/v3/core/types'
import {
  CloseIcon,
  ConfirmIcon,
  DeleteIcon,
  DragAndDropIcon,
  EditIcon,
  LaunchIcon,
} from 'src/v3/core/icons'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import {
  groupsInOrder,
  itemsInGroupOrder,
  moveItem,
} from 'src/v3/utilities/category-order'
import { hasNoTypes, templateTypeLabels } from 'src/v3/utilities/template-types'
import { useLinkedServiceSelection } from '../hooks/use-linked-service-selection'
import { useNotifications } from '../hooks/use-notifications'
import {
  fetchRecertCycleTemplateDetail,
  fetchRecertCycleTemplates,
  selectRecertCycleTemplateDetail,
  selectRecertCycleTemplateDetailStatus,
  selectRecertCycleTemplates,
  selectRecertCycleTemplatesStatus,
} from '../compliance.slice'
import TemplateCategoryForm from './components/template-category-form'
import TemplateCourseForm from './components/template-course-form'
import TemplateTypesForm from './components/template-types-form'

let CREATE_OPTION_ID = '__create__'
let GROUP_DRAG_TYPE = 'group'
let CATEGORY_DROPPABLE_PREFIX = 'category:'

type TemplateOption =
  RecertificationCycleTemplate | { id: typeof CREATE_OPTION_ID; name: string }

export default function ComplianceTemplates() {
  let dispatch = useAppDispatch()
  let { showError, NotificationComponent } = useNotifications()

  let linkedServiceID = useLinkedServiceSelection()
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let templates = useAppSelector(
    selectRecertCycleTemplates(linkedServiceID || ''),
  )
  let templatesStatus = useAppSelector(
    selectRecertCycleTemplatesStatus(linkedServiceID || ''),
  )

  let [selectedTemplate, setSelectedTemplate] =
    useState<RecertificationCycleTemplate | null>(null)
  let [templateDialogMode, setTemplateDialogMode] = useState<
    'create' | 'edit' | null
  >(null)
  let [templateName, setTemplateName] = useState('')
  let [templateDescription, setTemplateDescription] = useState('')
  let [savingTemplate, setSavingTemplate] = useState(false)

  let templateDetail = useAppSelector(
    selectRecertCycleTemplateDetail(selectedTemplate?.id || ''),
  )
  let templateDetailStatus = useAppSelector(
    selectRecertCycleTemplateDetailStatus(selectedTemplate?.id || ''),
  )

  let [categoryFormOpen, setCategoryFormOpen] = useState(false)
  let [categoryFormInfo, setCategoryFormInfo] =
    useState<Partial<RecertificationCycleTemplateCategory>>()
  let [courseFormOpen, setCourseFormOpen] = useState(false)
  let [courseFormInfo, setCourseFormInfo] =
    useState<Partial<RecertificationCycleTemplateCourse>>()
  let [courseFormCategoryID, setCourseFormCategoryID] = useState<string>()
  let [typesFormOpen, setTypesFormOpen] = useState(false)

  let [deletingCategoryID, setDeletingCategoryID] = useState<string>()
  let [isSavingOrder, setIsSavingOrder] = useState(false)
  let [deletingCourseID, setDeletingCourseID] = useState<string>()
  let [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState(false)
  let [deletingTemplate, setDeletingTemplate] = useState(false)

  let manageCourses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(coursesStatus, fetchCourses)

  useEffect(() => {
    if (
      linkedServiceID &&
      linkedServiceID !== 'unselected' &&
      (!templatesStatus || templatesStatus === 'uninitialized')
    ) {
      dispatch(fetchRecertCycleTemplates(linkedServiceID))
    }
  }, [linkedServiceID, templatesStatus])

  useEffect(() => {
    if (
      selectedTemplate?.id &&
      (!templateDetailStatus || templateDetailStatus === 'uninitialized')
    ) {
      dispatch(fetchRecertCycleTemplateDetail(selectedTemplate.id))
    }
  }, [selectedTemplate?.id, templateDetailStatus])

  // Clear selection when LS changes
  useEffect(() => {
    setSelectedTemplate(null)
  }, [linkedServiceID])

  let openCreateTemplateDialog = () => {
    setTemplateName('')
    setTemplateDescription('')
    setTemplateDialogMode('create')
  }

  let openEditTemplateDialog = () => {
    let template = templateDetail?.template ?? selectedTemplate
    setTemplateName(template?.name ?? '')
    setTemplateDescription(template?.description ?? '')
    setTemplateDialogMode('edit')
  }

  let handleSaveTemplate = () => {
    if (
      !templateName.trim() ||
      !linkedServiceID ||
      linkedServiceID === 'unselected'
    )
      return
    let fields = {
      name: templateName.trim(),
      description: templateDescription,
    }
    let request =
      templateDialogMode === 'edit' && selectedTemplate
        ? axios.put<RecertificationCycleTemplate>(
            '/api/manage/recertification-cycle/template',
            { id: selectedTemplate.id, ...fields },
          )
        : axios.post<RecertificationCycleTemplate>(
            '/api/manage/recertification-cycle/template',
            { ...fields, linkedServiceID },
          )
    setSavingTemplate(true)
    request
      .then(pluckData)
      .then((saved) => {
        setTemplateDialogMode(null)
        dispatch(fetchRecertCycleTemplates(linkedServiceID)).then(() => {
          setSelectedTemplate(saved)
        })
        dispatch(fetchRecertCycleTemplateDetail(saved.id))
      })
      .catch((err) => showError(pluckError(err, 'Error saving template')))
      .finally(() => setSavingTemplate(false))
  }

  let handleDeleteTemplate = () => {
    if (!selectedTemplate) return
    setDeletingTemplate(true)
    axios
      .delete(
        `/api/manage/recertification-cycle/template/${selectedTemplate.id}`,
      )
      .then(() => {
        setConfirmDeleteTemplate(false)
        setSelectedTemplate(null)
        if (linkedServiceID)
          dispatch(fetchRecertCycleTemplates(linkedServiceID))
      })
      .catch((err) => showError(pluckError(err, 'Error deleting template')))
      .finally(() => setDeletingTemplate(false))
  }

  let handleDeleteCategory = (categoryID: string) => {
    setDeletingCategoryID(categoryID)
    axios
      .delete(
        `/api/manage/recertification-cycle/template/category/${categoryID}`,
      )
      .then(() => {
        if (selectedTemplate?.id)
          dispatch(fetchRecertCycleTemplateDetail(selectedTemplate.id))
      })
      .catch((err) => showError(pluckError(err, 'Error deleting category')))
      .finally(() => setDeletingCategoryID(undefined))
  }

  let handleDeleteCourse = (courseID: string) => {
    setDeletingCourseID(courseID)
    axios
      .delete(`/api/manage/recertification-cycle/template/course/${courseID}`)
      .then(() => {
        if (selectedTemplate?.id)
          dispatch(fetchRecertCycleTemplateDetail(selectedTemplate.id))
      })
      .catch((err) => showError(pluckError(err, 'Error deleting course')))
      .finally(() => setDeletingCourseID(undefined))
  }

  let refetchDetail = () => {
    if (selectedTemplate?.id)
      dispatch(fetchRecertCycleTemplateDetail(selectedTemplate.id))
  }

  let categories = templateDetail?.categories ?? []
  let assignedCourses = categories.flatMap((c) => c.courses)

  // Shown in local order so a drag lands at once; the server's order takes over once saved.
  let [localCategories, setLocalCategories] = useState(categories)

  useEffect(() => {
    if (!isSavingOrder)
      setLocalCategories(
        categories.toSorted(
          (a, b) => a.category.sequence - b.category.sequence,
        ),
      )
  }, [templateDetail, isSavingOrder])

  let groupOf = (c: (typeof categories)[number]) =>
    c.category.subcategory || 'Other'

  let groups = groupsInOrder(
    localCategories,
    groupOf,
    (c) => c.category.sequence,
  )
  let hasGroups = groups.some((g) => g !== 'Other')

  // Renumbers the whole template in the given order: groups and their categories move together.
  let saveOrder = (reordered: typeof categories) => {
    if (!selectedTemplate) return
    setLocalCategories(
      reordered.map((c, i) => ({
        ...c,
        category: { ...c.category, sequence: i },
      })),
    )
    setIsSavingOrder(true)
    axios
      .put(
        `/api/manage/recertification-cycle/template/${selectedTemplate.id}/category/sequence`,
        {
          categories: reordered.map((c, i) => ({
            categoryID: c.category.id,
            sequence: i,
          })),
        },
      )
      .catch((err) =>
        showError(pluckError(err, 'Failed to update category order')),
      )
      // Saving lasts until the refetch lands (as in Create v5), so the old order never flashes back.
      .then(() => dispatch(fetchRecertCycleTemplateDetail(selectedTemplate.id)))
      .finally(() => setIsSavingOrder(false))
  }

  // A group drags as a block; a category moves within its group.
  let handleCategoryDragEnd = (result: DropResult) => {
    let { source, destination } = result
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    )
      return
    if (result.type === GROUP_DRAG_TYPE) {
      saveOrder(
        itemsInGroupOrder(
          localCategories,
          moveItem(groups, source.index, destination.index),
          groupOf,
        ),
      )
      return
    }
    if (destination.droppableId !== source.droppableId) return
    let group = source.droppableId.slice(CATEGORY_DROPPABLE_PREFIX.length)
    let reorderedGroup = moveItem(
      localCategories.filter((c) => groupOf(c) === group),
      source.index,
      destination.index,
    )
    saveOrder(
      groups.flatMap((g) =>
        g === group
          ? reorderedGroup
          : localCategories.filter((c) => groupOf(c) === g),
      ),
    )
  }

  let templateOptions: TemplateOption[] = [
    { id: CREATE_OPTION_ID, name: 'Create Template' },
    ...(templates ?? []).filter((t) => !!t.linkedServiceID),
  ]

  return (
    <Container maxWidth="xl" className="py-6">
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Recertification Cycle Templates
      </Typography>

      {/* Search bar */}
      <Card
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ width: '100%' }}>
          Select Template
        </Typography>
        <FormControl sx={{ minWidth: 200, bgcolor: 'background.paper' }}>
          <InputLabel id="templates-linked-service-label">
            Linked Service
          </InputLabel>
          <Select
            labelId="templates-linked-service-label"
            label="Linked Service"
            value={linkedServiceID || ''}
            onChange={(e) =>
              dispatch(activeLinkedServiceChanged(e.target.value))
            }
          >
            {linkedServices.map((service) => (
              <MenuItem key={service.id} value={service.id}>
                {service.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          sx={{ flexGrow: 1, minWidth: 280, bgcolor: 'background.paper' }}
          options={templateOptions}
          getOptionLabel={(opt) =>
            opt.id === CREATE_OPTION_ID ? '' : opt.name
          }
          value={selectedTemplate}
          onChange={(_, opt) => {
            if (!opt) {
              setSelectedTemplate(null)
            } else if (opt.id === CREATE_OPTION_ID) {
              openCreateTemplateDialog()
            } else {
              setSelectedTemplate(opt as RecertificationCycleTemplate)
            }
          }}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          disabled={!linkedServiceID || linkedServiceID === 'unselected'}
          renderOption={(props, opt) => {
            if (opt.id === CREATE_OPTION_ID) {
              return (
                <li {...props} key={opt.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'primary.main',
                      fontWeight: 'medium',
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                    Create Template
                  </Box>
                </li>
              )
            }
            return (
              <li {...props} key={opt.id}>
                {opt.name}
              </li>
            )
          }}
          renderInput={(params) => <TextField {...params} label="Template" />}
        />
      </Card>

      {/* Template detail */}
      {selectedTemplate && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5">
                {templateDetail?.template.name}
              </Typography>
              <IconButton
                aria-label="Edit template"
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider' }}
                onClick={openEditTemplateDialog}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="error"
                aria-label="Delete template"
                size="small"
                sx={{ border: '1px solid', borderColor: 'error' }}
                onClick={() => setConfirmDeleteTemplate(true)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCategoryFormInfo(undefined)
                  setCategoryFormOpen(true)
                }}
              >
                Add Category
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={categories.length === 0}
                onClick={() => {
                  setCourseFormInfo(undefined)
                  setCourseFormCategoryID(undefined)
                  setCourseFormOpen(true)
                }}
              >
                Add Course
              </Button>
            </Box>
          </Box>

          {templateDetail?.template.description && (
            <Typography
              color="textSecondary"
              sx={{ mb: 2, whiteSpace: 'pre-line' }}
            >
              {templateDetail.template.description}
            </Typography>
          )}

          {templateDetail && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ mr: 0.5 }}>
                For:
              </Typography>
              {hasNoTypes(templateDetail.template.types) ? (
                <Typography color="text.secondary" variant="body2">
                  Every cycle
                </Typography>
              ) : (
                templateTypeLabels(templateDetail.template.types).map(
                  (label) => <Chip key={label} label={label} size="small" />,
                )
              )}
              {/* Global templates are FOAMfrat's; the API only lets Manage edit a linked service's. */}
              {templateDetail.template.linkedServiceID && (
                <Button size="small" onClick={() => setTypesFormOpen(true)}>
                  Edit Types
                </Button>
              )}
            </Box>
          )}

          {templateDetailStatus === 'pending' && (
            <Typography color="text.secondary">Loading…</Typography>
          )}

          <Box aria-live="polite" aria-atomic="true" className="sr-only">
            {isSavingOrder ? 'Saving…' : ''}
          </Box>
          {isSavingOrder && (
            <Box className="flex items-center gap-2 mb-2">
              <CircularProgress size={14} aria-label="Saving" />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Saving…
              </Typography>
            </Box>
          )}

          {categories.length === 0 && templateDetailStatus !== 'pending' && (
            <Box
              sx={{
                p: 4,
                bgcolor: 'background.paperDark',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Avatar
                sx={{ height: 72, width: 72, bgcolor: 'background.paper' }}
              >
                <CategoryIcon fontSize="large" color="primary" />
              </Avatar>
              <Typography variant="h6">No Categories Yet</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCategoryFormInfo(undefined)
                  setCategoryFormOpen(true)
                }}
              >
                Add First Category
              </Button>
            </Box>
          )}

          <DragDropContext onDragEnd={handleCategoryDragEnd}>
            <Droppable droppableId="groups" type={GROUP_DRAG_TYPE}>
              {(groupsProvided) => (
                <Box
                  {...groupsProvided.droppableProps}
                  ref={groupsProvided.innerRef}
                >
                  {groups.map((group, groupIndex) => (
                    <Draggable
                      key={group}
                      draggableId={`group:${group}`}
                      index={groupIndex}
                      isDragDisabled={isSavingOrder || !hasGroups}
                    >
                      {(groupProv) => (
                        <Box
                          ref={groupProv.innerRef}
                          {...groupProv.draggableProps}
                          sx={{ mb: 2 }}
                        >
                          {hasGroups && (
                            <Box
                              className="flex items-center gap-1"
                              sx={{ mb: 0.75, ml: 0.5 }}
                            >
                              <Box
                                {...groupProv.dragHandleProps}
                                aria-label={`Drag to reorder group ${group}`}
                                className="flex items-center"
                                sx={{
                                  cursor: isSavingOrder ? 'default' : 'grab',
                                  color: 'text.secondary',
                                }}
                              >
                                <DragAndDropIcon fontSize="small" />
                              </Box>
                              <Typography
                                color="text.secondary"
                                sx={{ fontWeight: 'bold' }}
                              >
                                {group}
                              </Typography>
                            </Box>
                          )}
                          <Droppable
                            droppableId={`${CATEGORY_DROPPABLE_PREFIX}${group}`}
                          >
                            {(provided) => (
                              <Box
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1.5,
                                }}
                              >
                                {localCategories
                                  .filter((c) => groupOf(c) === group)
                                  .map(({ category, courses }, index) => (
                                    <Draggable
                                      key={category.id}
                                      draggableId={category.id}
                                      index={index}
                                      isDragDisabled={isSavingOrder}
                                    >
                                      {(prov) => (
                                        <Card
                                          ref={prov.innerRef}
                                          {...prov.draggableProps}
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            bgcolor: 'background.paperDark',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              pb: 0.5,
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              alignItems: 'center',
                                              gap: 1,
                                              borderBottom: '1px solid',
                                              borderColor: 'divider',
                                            }}
                                          >
                                            <Box
                                              {...prov.dragHandleProps}
                                              aria-label={`Drag to reorder ${category.name}`}
                                              className="flex items-center"
                                              sx={{
                                                cursor: isSavingOrder
                                                  ? 'default'
                                                  : 'grab',
                                                color: 'text.secondary',
                                              }}
                                            >
                                              <DragAndDropIcon fontSize="small" />
                                            </Box>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Typography sx={{ fontSize: 18 }}>
                                                {category.name}
                                              </Typography>
                                            </Box>
                                            <Typography
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              {'•'}
                                            </Typography>
                                            <Typography
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              {category.hours} Hour
                                              {category.hours === 1
                                                ? ''
                                                : 's'}{' '}
                                              Required
                                            </Typography>
                                            <IconButton
                                              color="primary"
                                              size="small"
                                              aria-label={`Edit category: ${category.name}`}
                                              onClick={() => {
                                                setCategoryFormInfo(category)
                                                setCategoryFormOpen(true)
                                              }}
                                            >
                                              <EditIcon />
                                            </IconButton>
                                            <Box sx={{ flexGrow: 1 }} />
                                            <Button
                                              color="error"
                                              startIcon={<DeleteIcon />}
                                              disabled={
                                                deletingCategoryID ===
                                                category.id
                                              }
                                              onClick={() =>
                                                handleDeleteCategory(
                                                  category.id,
                                                )
                                              }
                                            >
                                              Delete Category
                                            </Button>
                                          </Box>

                                          <Box
                                            sx={{
                                              mt: 1,
                                              display: 'flex',
                                              flexDirection: 'column',
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                pl: 1,
                                                flexGrow: 1,
                                                color: 'text.secondary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                columnGap: 1,
                                                flexWrap: 'wrap',
                                              }}
                                            >
                                              <Typography
                                                sx={{ fontWeight: 'bold' }}
                                              >
                                                {courses.length === 0
                                                  ? 'No Courses Added'
                                                  : 'Course List'}
                                              </Typography>
                                              {courses.length > 0 &&
                                                (() => {
                                                  let assignedHours =
                                                    courses.reduce(
                                                      (sum, c) => sum + c.hours,
                                                      0,
                                                    )
                                                  let shortfall =
                                                    assignedHours <
                                                    category.hours
                                                  return (
                                                    <>
                                                      <Typography>
                                                        {'•'}
                                                      </Typography>
                                                      <Typography
                                                        sx={{
                                                          color:
                                                            'text.secondary',
                                                        }}
                                                      >
                                                        {assignedHours}/
                                                        {category.hours} Hours
                                                        Assigned
                                                      </Typography>
                                                      {shortfall && (
                                                        <Box
                                                          sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                              'center',
                                                            gap: 0.5,
                                                          }}
                                                        >
                                                          <Tooltip title="You don't need to fill out this category completely — users will be able to fulfill any remaining hours however they choose when completing their cycle.">
                                                            <Box
                                                              sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                  'center',
                                                                gap: 0.5,
                                                                cursor: 'help',
                                                              }}
                                                            >
                                                              <WarningIcon
                                                                fontSize="small"
                                                                sx={{
                                                                  color:
                                                                    'warning.main',
                                                                }}
                                                              />
                                                              <Typography
                                                                sx={{
                                                                  color:
                                                                    'warning.main',
                                                                }}
                                                              >
                                                                Assigned hours
                                                                do not meet the
                                                                category
                                                                requirement
                                                              </Typography>
                                                            </Box>
                                                          </Tooltip>
                                                        </Box>
                                                      )}
                                                    </>
                                                  )
                                                })()}
                                              <Box sx={{ flexGrow: 1 }} />
                                              <Button
                                                color="primary"
                                                size="small"
                                                onClick={() => {
                                                  setCourseFormInfo(undefined)
                                                  setCourseFormCategoryID(
                                                    category.id,
                                                  )
                                                  setCourseFormOpen(true)
                                                }}
                                              >
                                                Add Course
                                              </Button>
                                            </Box>

                                            {courses.length > 0 && (
                                              <Box
                                                sx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                }}
                                              >
                                                {courses.map(
                                                  (courseData, i) => {
                                                    let {
                                                      id,
                                                      courseID,
                                                      hours,
                                                    } = courseData
                                                    let manageCourse =
                                                      manageCourses?.find(
                                                        (c) =>
                                                          c.id === courseID,
                                                      )
                                                    return (
                                                      <Box
                                                        key={courseData.id}
                                                        sx={{
                                                          p: 1.5,
                                                          display: 'flex',
                                                          flexWrap: 'wrap',
                                                          alignItems: 'center',
                                                          gap: 0.5,
                                                          bgcolor:
                                                            i % 2
                                                              ? 'divider'
                                                              : undefined,
                                                        }}
                                                      >
                                                        <Typography>
                                                          {manageCourse?.name}
                                                        </Typography>
                                                        <Box
                                                          sx={{ flexGrow: 1 }}
                                                        >
                                                          {manageCourse && (
                                                            <IconButton
                                                              size="small"
                                                              color="primary"
                                                              aria-label={`Go to course ${manageCourse.name}`}
                                                              onClick={() =>
                                                                window.open(
                                                                  `/self-paced/course/${manageCourse.id}`,
                                                                  '_blank',
                                                                )
                                                              }
                                                            >
                                                              <LaunchIcon fontSize="small" />
                                                            </IconButton>
                                                          )}
                                                        </Box>
                                                        <Typography
                                                          sx={{ flexShrink: 0 }}
                                                        >
                                                          {hours} Hour
                                                          {hours === 1
                                                            ? ''
                                                            : 's'}
                                                        </Typography>
                                                        <Box
                                                          sx={{
                                                            my: -1,
                                                            ml: 1.5,
                                                            mr: -0.5,
                                                            flexShrink: 0,
                                                          }}
                                                        >
                                                          <IconButton
                                                            size="small"
                                                            aria-label={`Edit course${manageCourse?.name ? `: ${manageCourse.name}` : ''}`}
                                                            onClick={() => {
                                                              setCourseFormInfo(
                                                                courseData,
                                                              )
                                                              setCourseFormCategoryID(
                                                                category.id,
                                                              )
                                                              setCourseFormOpen(
                                                                true,
                                                              )
                                                            }}
                                                          >
                                                            <EditIcon fontSize="small" />
                                                          </IconButton>
                                                          <IconButton
                                                            size="small"
                                                            aria-label={`Delete course${manageCourse?.name ? `: ${manageCourse.name}` : ''}`}
                                                            disabled={
                                                              deletingCourseID ===
                                                              id
                                                            }
                                                            onClick={() =>
                                                              handleDeleteCourse(
                                                                id,
                                                              )
                                                            }
                                                          >
                                                            <DeleteIcon fontSize="small" />
                                                          </IconButton>
                                                        </Box>
                                                      </Box>
                                                    )
                                                  },
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        </Card>
                                      )}
                                    </Draggable>
                                  ))}
                                {provided.placeholder}
                              </Box>
                            )}
                          </Droppable>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {groupsProvided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      )}

      {!selectedTemplate && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Typography color="text.secondary">
            Search for a template above to view or edit it.
          </Typography>
        </Box>
      )}

      {/* Delete template confirmation */}
      <Dialog
        open={confirmDeleteTemplate}
        onClose={() => setConfirmDeleteTemplate(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{selectedTemplate?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<ConfirmIcon />}
            loading={deletingTemplate}
            onClick={handleDeleteTemplate}
            sx={{ flex: '1 1 0px' }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            color="inherit"
            startIcon={<CloseIcon />}
            onClick={() => setConfirmDeleteTemplate(false)}
            sx={{ flex: '1 1 0px' }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/edit template dialog */}
      <Dialog
        open={templateDialogMode !== null}
        onClose={() => setTemplateDialogMode(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {templateDialogMode === 'edit' ? 'Edit Template' : 'Create Template'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Description (optional)"
            helperText="Shown on cycles built from this template."
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTemplateDialogMode(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            loading={savingTemplate}
            disabled={
              !templateName.trim() ||
              !linkedServiceID ||
              linkedServiceID === 'unselected'
            }
          >
            {templateDialogMode === 'edit' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <TemplateCategoryForm
        templateID={selectedTemplate?.id || ''}
        category={categoryFormInfo}
        existingCategories={categories.map((c) => c.category)}
        open={categoryFormOpen}
        onClose={() => {
          setCategoryFormOpen(false)
          setCategoryFormInfo(undefined)
        }}
        onSaved={refetchDetail}
      />

      <TemplateTypesForm
        templateID={selectedTemplate?.id || ''}
        types={templateDetail?.template.types}
        open={typesFormOpen}
        onClose={() => setTypesFormOpen(false)}
        onSaved={() => {
          refetchDetail()
          // The compliance form filters its template options by the list's types.
          if (linkedServiceID && linkedServiceID !== 'unselected')
            dispatch(fetchRecertCycleTemplates(linkedServiceID))
        }}
      />

      <TemplateCourseForm
        templateCategoryID={courseFormCategoryID}
        course={courseFormInfo}
        categories={categories.map((c) => c.category)}
        existingCourses={assignedCourses}
        open={courseFormOpen}
        onClose={() => {
          setCourseFormOpen(false)
          setCourseFormInfo(undefined)
          setCourseFormCategoryID(undefined)
        }}
        onSaved={refetchDetail}
      />

      {NotificationComponent}
    </Container>
  )
}
